import { Ribbon } from '@99designs/common/components/Ribbon';
import { Attribution } from 'common/components/Attribution';
import { DesignCard } from 'common/components/DesignCard';
import Heading from 'common/components/Heading';
import { LogoDesignIcon } from 'common/components/LogoDesignIcon';
import Paragraph from 'common/components/Paragraph';
import { Currency, toUnit } from 'common/utils/currency';
import { BundleOption, BundleOptionKey } from 'interactive/bundle-options';
import React from 'react';
import { FormattedNumber } from 'react-intl';
import { AttributionPositioner, Item, ItemCard, ItemInput, ItemLabel } from './Item';
import useDelayedEffect from './useDelayedEffect';

const CurrencyCaption = ({
    caption,
    amountInCents,
    currency,
}: {
    caption: string;
    amountInCents: number;
    currency: Currency;
}) => (
    <>
        {`${caption} `}
        <FormattedNumber
            value={toUnit(amountInCents, currency) || 0}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
            maximumFractionDigits={0}
        />
    </>
);

interface Props {
    bundleOptions: BundleOption[];
    currentValue: BundleOptionKey;
    onSelect(key: BundleOptionKey): void;
    hasAnimation: boolean;
    resetAnimation: () => void;
}

const BundleSelect = ({
    currentValue,
    onSelect,
    bundleOptions,
    hasAnimation,
    resetAnimation,
}: Props) => {
    useDelayedEffect(resetAnimation, [hasAnimation]);

    return (
        <div className="get-started-interactive-content--bundle-select">
            <div className="get-started-interactive-content__inner">
                {bundleOptions.map(bundle => (
                    <Item hasAnimation={hasAnimation} key={bundle.key}>
                        <ItemLabel htmlFor={`checkbox-bundle-option-${bundle.key}`}>
                            <ItemInput
                                type="checkbox"
                                name="bundle-option"
                                value={bundle.key}
                                id={`checkbox-bundle-option-${bundle.key}`}
                                checked={currentValue === bundle.key}
                                onChange={({
                                    target: { value },
                                }: React.ChangeEvent<HTMLInputElement>) =>
                                    onSelect(value as BundleOptionKey)
                                }
                            />
                            <ItemCard>
                                {bundle.image && bundle.image.src ? (
                                    <DesignCard
                                        src={`/start/dist/${bundle.image.src}`}
                                        alt={`${bundle.key} design`}
                                        ribbon={
                                            (bundle.savingsInCents || bundle.savingsText) && (
                                                <Ribbon variations={['large']}>
                                                    {bundle.savingsText ? (
                                                        bundle.savingsText
                                                    ) : (
                                                        <CurrencyCaption
                                                            caption="Save up to"
                                                            amountInCents={bundle.savingsInCents}
                                                            currency={bundle.currency}
                                                        />
                                                    )}
                                                </Ribbon>
                                            )
                                        }
                                        width={224}
                                        height={224}
                                    />
                                ) : (
                                    <div className="get-started-interactive-content--item-card-icon">
                                        <LogoDesignIcon />
                                    </div>
                                )}
                                <div className="get-started-interactive-content--item-card-header-block">
                                    <Heading size="h5" variations={['h5', 'quarter-margin']}>
                                        {bundle.title}
                                    </Heading>
                                </div>{' '}
                                <div className="get-started-interactive-content--item-card-text-block">
                                    <Paragraph size="small" color="soft" spacing="no-margin">
                                        <CurrencyCaption
                                            caption="from"
                                            amountInCents={bundle.amountInCents}
                                            currency={bundle.currency}
                                        />
                                    </Paragraph>
                                </div>{' '}
                                {bundle.image && bundle.image.designer && (
                                    <AttributionPositioner>
                                        <Attribution name={bundle.image.designer} />
                                    </AttributionPositioner>
                                )}
                            </ItemCard>
                        </ItemLabel>
                    </Item>
                ))}
            </div>
        </div>
    );
};

export default BundleSelect;
